import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BellIcon, MailIcon, MessageSquareIcon, SmartphoneIcon, XIcon } from 'lucide-react';
import { materials, type MaterialId } from '../../data/collectes';
const leadTimes = ['1 hour in advance', '2 hours in advance', 'The evening before', 'The morning of'];
const channels: {
  id: string;
  label: string;
  Icon: typeof MailIcon;
}[] = [{
  id: 'email',
  label: 'Email',
  Icon: MailIcon
}, {
  id: 'sms',
  label: 'Text message',
  Icon: MessageSquareIcon
}, {
  id: 'push',
  label: 'Mobile notification',
  Icon: SmartphoneIcon
}];
const dots: Record<MaterialId, string> = {
  ordures: 'bg-[#475569]',
  recyclage: 'bg-[#0f766e]',
  organiques: 'bg-[#3f6212]',
  verts: 'bg-[#a16207]'
};
const materialIds = Object.keys(materials) as MaterialId[];
interface ReminderPreferencesProps {
  onClose: () => void;
}
export function ReminderPreferences({
  onClose
}: ReminderPreferencesProps) {
  const [leadTime, setLeadTime] = useState(leadTimes[0]);
  const [activeChannels, setActiveChannels] = useState<string[]>(['email']);
  const [activeMaterials, setActiveMaterials] = useState<MaterialId[]>(['ordures']);
  const toggle = <T,>(list: T[], value: T): T[] => list.includes(value) ? list.filter(item => item !== value) : [...list, value];
  return <motion.section aria-labelledby="reminder-preferences-title" initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="order-first mb-6 rounded-3xl border border-slate-200 bg-white p-6 lg:order-none lg:mb-0 lg:mt-6" data-id="element-695" data-mp="35kmc8ki7srb8">
      <div className="flex items-start justify-between gap-4" data-id="element-696" data-mp="3oif2am7qp6r7">
        <div data-id="element-697" data-mp="3oi6psvfj43gj">
          <h3 id="reminder-preferences-title" className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-698" data-mp="3ogcf09xz1rq6">
            <BellIcon className="h-5 w-5 text-ville-blue" aria-hidden="true" data-id="element-699" data-mp="k3p0urnmtnab" />
            Collection reminders
          </h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-700" data-mp="2eddrbe7blhfp">Choose when and how you want to be reminded.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close the reminder preferences" className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-701" data-mp="2jam7584zx9ds">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-702" data-mp="20ko3d488e2fy" />
        </button>
      </div>

      <div className="mt-5" data-id="element-703" data-mp="20il6q1ckpb29">
        <label htmlFor="reminder-lead-time" className="text-sm font-bold text-ville-navy" data-id="element-704" data-mp="1lnnct41s0dj2">
          Send my reminder
        </label>
        <select id="reminder-lead-time" value={leadTime} onChange={event => setLeadTime(event.target.value)} className="mt-2 w-full max-w-xs rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-705" data-mp="1qn9ova5vbnqa">
          {leadTimes.map(option => <option key={option} value={option} data-id="element-706" data-mp="14fnrb64inaha">
              {option}
            </option>)}
        </select>
      </div>

      <fieldset className="mt-6" data-id="element-707" data-mp="1j5212fc07tx7">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-708" data-mp="1eciljctraopr">How should we reach you?</legend>
        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3" data-id="element-709" data-mp="1s3dqie14wlo2">
          {channels.map(({
          id,
          label,
          Icon
        }) => <label key={id} className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ville-navy" data-id="element-710" data-mp="1o1acpiqzt4zm">
              <input type="checkbox" checked={activeChannels.includes(id)} onChange={() => setActiveChannels(current => toggle(current, id))} className="h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-711" data-mp="2211uzvojl7jf" />
              <Icon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-712" data-mp="2opfoh86zbs5" />
              {label}
            </label>)}
        </div>
      </fieldset>

      <fieldset className="mt-6" data-id="element-713" data-mp="4537b8oi6gvm">
        <legend className="text-sm font-bold text-ville-navy" data-id="element-714" data-mp="488vmlpb5eqa">Which collections do you want reminders for?</legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-715" data-mp="e42fkq5cwai3">
          {materialIds.map(id => <label key={id} className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-ville-navy" data-id="element-716" data-mp="e1zkvst9dt71">
              <input type="checkbox" checked={activeMaterials.includes(id)} onChange={() => setActiveMaterials(current => toggle(current, id))} className="h-4 w-4 rounded border-slate-300 text-ville-blue focus:ring-ville-indigo" data-id="element-717" data-mp="3hl153frkgr3y" />
              <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dots[id]}`} aria-hidden="true" data-id="element-718" data-mp="3rhxgbt8unrc7" />
              {materials[id].label}
            </label>)}
        </div>
      </fieldset>

      <div className="mt-6 flex items-center justify-end gap-3" data-id="element-719" data-mp="2yv2yl43f362f">
        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-720" data-mp="2ysgp2ywyughk">
          Cancel
        </button>
        <button type="button" onClick={onClose} className="rounded-full bg-ville-navy px-6 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-721" data-mp="1guk1qued6z77">
          Save reminders
        </button>
      </div>
    </motion.section>;
}